import { ComponentProps, useState } from 'react';
import { VariantProps } from 'class-variance-authority';

import { mergeClasses } from '@/utils';

import { Label } from '@/components/common';

import { BORDERED_BUTTON_GROUP_VARIANTS } from './BorderedButtonGroup.constants';

interface IBorderedButtonGroup extends Omit<ComponentProps<'div'>, 'onSelect'> {
  label?: string;
  required?: boolean;
  buttonNames: { name: string; value: number | string }[];
  selectedValue?: number | string;
  buttonClass?: string;
  disabled?: boolean;
  buttonColor?: VariantProps<typeof BORDERED_BUTTON_GROUP_VARIANTS>['buttonColor'];
  error?: string;
  onSelect?: (value: number | string) => void;
}

const BorderedButtonGroup = ({
  className,
  label,
  required,
  buttonNames,
  selectedValue: controlledSelectedValue,
  buttonClass,
  onSelect,
  disabled,
  buttonColor,
  error,
  ...props
}: IBorderedButtonGroup) => {
  const [selectedButtonValue, setSelectedButtonValue] = useState(controlledSelectedValue);
  const selectedValue = controlledSelectedValue ?? selectedButtonValue;

  return (
    <div className="flex flex-col gap-2">
      {label && <Label required={required}>{label}</Label>}
      <div
        role="group"
        data-slot="button-group"
        className={mergeClasses(
          BORDERED_BUTTON_GROUP_VARIANTS({ buttonColor }),
          disabled && '!outline-neutral-6',
          error && '!outline-semantic-error-base',
          className,
        )}
        {...props}
      >
        {buttonNames.map((buttonName) => (
          <button
            key={buttonName.value}
            data-selected={selectedValue === buttonName.value}
            aria-disabled={disabled}
            className={mergeClasses(
              'font-semibold text-body-sm min-w-[120px]',
              selectedValue === buttonName.value
                ? '!text-neutral-0 aria-disabled:!bg-neutral-6'
                : '!bg-neutral-0 dark:!bg-neutral-10 aria-disabled:!text-neutral-6',
              disabled && 'cursor-not-allowed !border-neutral-6',
              error && '!border-semantic-error-base',
              buttonClass,
            )}
            onClick={() => {
              if (disabled) return;
              setSelectedButtonValue(buttonName.value);
              onSelect?.(buttonName.value);
            }}
            disabled={disabled}
          >
            {buttonName.name}
          </button>
        ))}
      </div>
      {error && <p className="text-body-xs text-semantic-error-base font-medium">{error}</p>}
    </div>
  );
};

export { BorderedButtonGroup };
